import { ADMIN_API_PREFIX, route } from "./routes.js";
import { z } from "zod";
import { SURFACE_NAME_MAX_LENGTH, SURFACE_NAME_PATTERN, mountPathSchema } from "@zion/sdk-contracts";
//#region src/root-shell.ts
/**
* `/v1/root-shell`, `/v1/root-shell/settings` and `/v1/admin/root-shell`: which root shell a URL
* gets, the root shell's tenant settings, and the org admin's path and hostname assignments
* (layered shell surfaces, D22, R-12, R-17, R-25 d, R-34, R-60).
*
* Producers: `RootShellController` and `AdminRootShellController` in
* `cloud/dp/core-api/src/root-shell/` (WS-5). Consumers: the shell loader's root resolution
* (WS-6) reads the public route before any credential exists; the loader's settings source
* (WS-8's `LoaderServiceDeps.shellSettings`) reads the settings route once per session; an org
* admin writes the admin route through `PUT` (R-18: no screen in this feature).
*
* One writer per row class (R-34). The CP is the one writer of an org's `/` row, through
* core-api's `/internal/shell-sync` (`ShellSyncRequestSchema` in `@zion/sdk-cp-dp-contracts`).
* `PUT /v1/admin/root-shell` writes the path rows such as `/alt` (R-17) and the hostname rows of
* stage 4 (R-12), and refuses `pathPrefix: '/'` with {@link ROOT_SHELL_ROOT_PATH_MESSAGE}.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped rather than refused. Every request schema is `z.strictObject`, so a key the server
* would reject is refused before the request leaves the client. No shape in this file travels in
* both directions.
*
* No `.transform()` appears here, so each schema stays representable for
* `createOpenApiDocument()` when core-api binds it through `@Query({ schema })` or
* `@Body({ schema })`.
*
* This module does no I/O and holds no state.
*/
/** The most rows one `PUT /v1/admin/root-shell` body may carry, path and hostname rows together. */
const MAX_ROOT_SHELL_ASSIGNMENTS = 32;
/** The longest `path` query value `GET /v1/root-shell` accepts, in characters. */
const ROOT_SHELL_QUERY_PATH_MAX_LENGTH = 2048;
/** The longest `appId` a row or an answer carries, in characters. */
const ROOT_SHELL_APP_ID_MAX_LENGTH = 128;
/** The longest hostname, in characters (RFC 1035's 253; `ROOT_SHELL_HOST_MAX_LENGTH` in `@zion/sdk-contracts` holds the same value). */
const ROOT_SHELL_ASSIGNMENT_HOST_MAX_LENGTH = 253;
/**
* The message a `pathPrefix: '/'` row earns at `PUT /v1/admin/root-shell` (R-34). It travels in
* the 400's `message` array beside `mountPathSchema`'s own message.
*/
const ROOT_SHELL_ROOT_PATH_MESSAGE = "pathPrefix '/' is set on the CP tenant pane and synced to this DP; the admin route writes other paths and hostnames only (R-34)";
/** The message a hostname outside {@link ROOT_SHELL_HOST_PATTERN} earns. */
const ROOT_SHELL_HOST_MESSAGE = "host must be a lowercase DNS name of two or more labels, with no port, no trailing dot and no IP literal";
/**
* The hostname grammar: two or more labels of `[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?` joined by
* dots, and a last label that is not all digits, so an IPv4 literal is refused. It is the
* `grammar` arm of `refusalFor` in `@zion/sdk-contracts` minus the length test, which
* {@link rootShellHostSchema} runs first; the test suite holds the two to one verdict. A port, an
* upper-case letter, a trailing dot, a `*` and an IPv6 literal all fail it.
*/
const ROOT_SHELL_HOST_PATTERN = "^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\\.)+(?![0-9]+$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$";
/** The grammar of the public route's `path` query: a leading `/`, then no `?`, `#` or whitespace. */
const ROOT_SHELL_QUERY_PATH_PATTERN = "^/[^?#\\s]*$";
/** A surface name (`shell.v1`): `SURFACE_NAME_PATTERN`, at most `SURFACE_NAME_MAX_LENGTH` characters. */
const rootShellSurfaceNameSchema = z.string().max(SURFACE_NAME_MAX_LENGTH).regex(new RegExp(SURFACE_NAME_PATTERN));
/** The app slug a row names: 1 to {@link ROOT_SHELL_APP_ID_MAX_LENGTH} characters. Membership of `ROOT_SHELL_APPS` is core-api's check, with its own 400. */
const rootShellAppIdSchema = z.string().min(1).max(128);
/**
* A path row's prefix: a `mountPathSchema` value, which already refuses `/`, a trailing slash,
* upper case and every reserved first segment. A `'/'` value also earns
* {@link ROOT_SHELL_ROOT_PATH_MESSAGE}, so the 400 names R-34.
*/
const rootShellPathPrefixSchema = mountPathSchema.refine((prefix) => prefix !== "/", { message: ROOT_SHELL_ROOT_PATH_MESSAGE });
/** A hostname row's host: at most 253 characters, then {@link ROOT_SHELL_HOST_PATTERN}. */
const rootShellHostSchema = z.string().max(253).regex(new RegExp(ROOT_SHELL_HOST_PATTERN), { message: ROOT_SHELL_HOST_MESSAGE });
/**
* `GET /v1/root-shell?path=<path>`. Public (`@Public()`, `@AnonymousRoute`), `Cache-Control:
* no-store`. core-api resolves the org from `Host`, then picks the verified hostname row for that
* host, else the path row with the longest prefix `path` lies under. The status set:
*
* - 200 {@link getRootShellResponseSchema}: the org has a matching row.
* - 204 and no body: an org owns the host, and the org has no matching row. This is the expected
*   answer for an org with no `/` row. It is a success status, so the browser logs no failed
*   request for it.
* - 404 `no org for this host`: no org owns the host.
* - 400 on an unknown query key or a `path` outside the grammar; 503 while setup is open; 429
*   over the global rate.
*
* The loader reads every status other than 200 as "no assignment" and mounts `default-shell`
* (D22). A core-api from before this rule answers 404 `no root shell assigned` for the 204 case,
* and the loader reads that 404 the same way, so an old core-api and a new loader agree.
*/
const GET_ROOT_SHELL_ROUTE = route("GET", "/v1/root-shell");
/**
* The public query. `path` is the browser path the loader is resolving, `/` when absent; at most
* {@link ROOT_SHELL_QUERY_PATH_MAX_LENGTH} characters and {@link ROOT_SHELL_QUERY_PATH_PATTERN}.
*/
const getRootShellQuerySchema = z.strictObject({ path: z.string().max(ROOT_SHELL_QUERY_PATH_MAX_LENGTH).regex(new RegExp(ROOT_SHELL_QUERY_PATH_PATTERN)).optional() });
/**
* The 200 body: the app slug and the root surface to mount, and nothing else about the org. The
* loader parses it and falls back to `default-shell` on a refusal, so a malformed answer never
* mounts. The 204 answer has no body, and this schema refuses an absent or empty body, so a
* client that parses without a status check also falls back.
*/
const getRootShellResponseSchema = z.object({
	appId: rootShellAppIdSchema,
	surface: rootShellSurfaceNameSchema
});
/**
* `GET /v1/root-shell/settings` (R-60). Guarded by `DpJwtAuthGuard` alone, as `GET /v1/auth/me`
* is: any signed-in member of the org may read it, and no permission key applies. The org comes
* from the verified JWT only; the route takes no query, no parameter and no body, so a caller can
* name no other org. `Cache-Control: no-store`. 200 {@link getRootShellSettingsResponseSchema}
* always for a signed-in caller; 401 without a valid session.
*
* The settings are the `settings` member of the org's last accepted `POST /internal/shell-sync`
* body, stored on the org's `/` row of `auth.root_shell_assignments`. The CP is their one writer
* (R-34). `GET /v1/root-shell` stays `{appId, surface}` only, so the public route discloses no
* tenant setting before sign-in.
*/
const GET_ROOT_SHELL_SETTINGS_ROUTE = route("GET", "/v1/root-shell/settings");
/**
* The 200 body of {@link GET_ROOT_SHELL_SETTINGS_ROUTE}.
*
* - `settings: null`: the org has no `/` row, so no stored settings. Every CP row names a
*   `rootShell` (R-78), so this means the CP has not pushed the org's shell setting to this data
*   plane yet. The root surface reads `settings: null` and runs on its declared defaults.
* - `settings: {...}`: the object the sync stored. A sync body that omitted `settings` stores
*   `{}`, because `OrgShellSchema` reads an omitted `settings` as `{}`.
*
* The values are opaque here. core-api parsed them with `ShellSettingsSchema` from
* `@zion/sdk-cp-dp-contracts` before it stored them, and the shell parses the member with that
* schema again before any surface reads it, so this package names no second copy of its bounds.
* `z.object` per the direction rule: a member core-api adds later is stripped.
*/
const getRootShellSettingsResponseSchema = z.object({ settings: z.record(z.string(), z.unknown()).nullable() });
/** The admin collection path. No parameter, so the OpenAPI form and the request form are the same. */
const ADMIN_ROOT_SHELL_PATH = `${ADMIN_API_PREFIX}/root-shell`;
/**
* `GET /v1/admin/root-shell`. Every assignment row of the caller's org, hostname rows by `host`
* then path rows by `pathPrefix`; 200 `{assignments: []}` for an org with none, never 404. The
* CP-written `/` row is in the list with `source: 'cp'` (R-60), so the admin sees which root shell
* `/` gets; every other row carries `source: 'admin'`. Permission `settings:manage`. The org
* comes from the JWT only.
*/
const GET_ADMIN_ROOT_SHELL_ROUTE = route("GET", ADMIN_ROOT_SHELL_PATH);
/**
* `PUT /v1/admin/root-shell`. Replaces the org's path and hostname rows with the body's, and
* answers 200 with the stored set in {@link adminRootShellAssignmentsResponseSchema}. `[]` clears
* them; the CP's `/` row is never in the body and never touched (R-34). 400 from the pipe on a
* body outside {@link putAdminRootShellRequestSchema}, then core-api's own 400s (duplicate
* target, an app outside `ROOT_SHELL_APPS`, a host another org resolves to, an app the org cannot
* use, a surface that is not the app's root, a fifth hostname row, a `refusalFor` refusal) and 409
* `host already assigned`. A hostname row is stored pending until its TXT record proves it.
* Permission `settings:manage`.
*/
const PUT_ADMIN_ROOT_SHELL_ROUTE = route("PUT", ADMIN_ROOT_SHELL_PATH);
/** A path row of the `PUT` body: `pathPrefix`, `appId`, `surface`, and no other key. */
const rootShellPathAssignmentSchema = z.strictObject({
	pathPrefix: rootShellPathPrefixSchema,
	appId: rootShellAppIdSchema,
	surface: rootShellSurfaceNameSchema
});
/** A hostname row of the `PUT` body: `host`, `appId`, `surface`, and no other key. */
const rootShellHostAssignmentSchema = z.strictObject({
	host: rootShellHostSchema,
	appId: rootShellAppIdSchema,
	surface: rootShellSurfaceNameSchema
});
/**
* One `PUT` row: a path row or a hostname row. A row carrying both `pathPrefix` and `host`, or
* neither, fails both arms and is refused. zod reports the issues of the arm the row is closest
* to, so a `pathPrefix: '/'` row's 400 carries {@link ROOT_SHELL_ROOT_PATH_MESSAGE}.
*/
const rootShellAssignmentRequestRowSchema = z.union([rootShellPathAssignmentSchema, rootShellHostAssignmentSchema]);
/** The `PUT` body: at most {@link MAX_ROOT_SHELL_ASSIGNMENTS} rows. An `organizationId` key is refused. */
const putAdminRootShellRequestSchema = z.strictObject({ assignments: z.array(rootShellAssignmentRequestRowSchema).max(32) });
/**
* The DNS TXT record that proves an org controls a hostname: publish `value` at `name`. `name` is
* `_zion-root-shell.<host>`; `value` is derived from the org and the host by core-api's
* `hostVerificationRecord` and holds no secret.
*/
const rootShellHostVerificationSchema = z.object({
	name: z.string(),
	value: z.string()
});
/**
* Who wrote a stored row (R-34, R-60): `cp` for the org's `/` row, which only
* `POST /internal/shell-sync` writes from the CP tenant setting; `admin` for every path and
* hostname row, which only `PUT /v1/admin/root-shell` writes.
*/
const ROOT_SHELL_ASSIGNMENT_SOURCES = Object.freeze(["cp", "admin"]);
/**
* One stored row as the admin reads it. Exactly one of `host` and `pathPrefix` is non-null.
* `source` names the row's writer: `cp` on the `/` row, `admin` on every other row; a client
* that sends the listed set back through `PUT` must drop the `cp` row, or the R-34 400 answers.
* `assignedBy` is the admin's user id, or null when nobody is recorded (always null on the `cp`
* row). `assignedAt` and `verifiedAt` are ISO-8601 instants. A hostname row carries its
* `verification` record, and `verifiedAt` stays null until core-api resolves that record (a
* pending row matches nothing, R-25 d). A path row carries `verifiedAt: null` and
* `verification: null`.
*/
const rootShellAssignmentViewSchema = z.object({
	source: z.enum(ROOT_SHELL_ASSIGNMENT_SOURCES),
	host: z.string().nullable(),
	pathPrefix: z.string().nullable(),
	appId: z.string(),
	surface: z.string(),
	assignedBy: z.string().uuid().nullable(),
	assignedAt: z.iso.datetime(),
	verifiedAt: z.iso.datetime().nullable(),
	verification: rootShellHostVerificationSchema.nullable()
});
/** The 200 body of both admin routes. */
const adminRootShellAssignmentsResponseSchema = z.object({ assignments: z.array(rootShellAssignmentViewSchema) });
/**
* The three routes of this file that `route-census.ts` folds as its `root-shell` family (R-60:
* the census moves 103 to 106). {@link GET_ROOT_SHELL_SETTINGS_ROUTE} is outside this list,
* because R-60 counts these three; whether it joins the census is an open escalation
* (`reports/S2-WS-5-contracts.md`).
*/
const rootShellRoutes = Object.freeze([
	GET_ROOT_SHELL_ROUTE,
	GET_ADMIN_ROOT_SHELL_ROUTE,
	PUT_ADMIN_ROOT_SHELL_ROUTE
]);
//#endregion
export { ADMIN_ROOT_SHELL_PATH, GET_ADMIN_ROOT_SHELL_ROUTE, GET_ROOT_SHELL_ROUTE, GET_ROOT_SHELL_SETTINGS_ROUTE, MAX_ROOT_SHELL_ASSIGNMENTS, PUT_ADMIN_ROOT_SHELL_ROUTE, ROOT_SHELL_APP_ID_MAX_LENGTH, ROOT_SHELL_ASSIGNMENT_HOST_MAX_LENGTH, ROOT_SHELL_ASSIGNMENT_SOURCES, ROOT_SHELL_HOST_MESSAGE, ROOT_SHELL_HOST_PATTERN, ROOT_SHELL_QUERY_PATH_MAX_LENGTH, ROOT_SHELL_QUERY_PATH_PATTERN, ROOT_SHELL_ROOT_PATH_MESSAGE, adminRootShellAssignmentsResponseSchema, getRootShellQuerySchema, getRootShellResponseSchema, getRootShellSettingsResponseSchema, putAdminRootShellRequestSchema, rootShellAppIdSchema, rootShellAssignmentRequestRowSchema, rootShellAssignmentViewSchema, rootShellHostAssignmentSchema, rootShellHostSchema, rootShellHostVerificationSchema, rootShellPathAssignmentSchema, rootShellPathPrefixSchema, rootShellRoutes, rootShellSurfaceNameSchema };

//# sourceMappingURL=root-shell.js.map